/**
 * Lift2Fit — recent sessions, the compact proof that a finished session landed.
 *
 * `GET /sessions` returns the account's newest sessions first (`{ data,
 * nextCursor, hasMore }`, where each entry is a totals row). This renders the
 * first page as a plain list under the Grid: date, label, sets, volume and
 * status — never a dashboard. A completed session is clearly finished and an
 * abandoned one is marked as such, because both are real rows in the log.
 */
import { useEffect, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import { api, type SessionTotalsWire } from '@/lib/api';
import { formatDateTime, pluralize } from '@/lib/format';
import { formatVolume } from '@/lib/units';
import { StatusChip, type ChipTone } from './components';
import { Link } from './router';

const STATUS_META: Record<SessionTotalsWire['status'], { label: string; tone: ChipTone }> = {
  completed: { label: 'Finished', tone: 'success' },
  abandoned: { label: 'Abandoned', tone: 'danger' },
  active: { label: 'In progress', tone: 'accent' },
};

export interface RecentSessionsListProps {
  unit: UnitSystem;
  /** changing this value re-reads the list (bumped when a session ends) */
  refreshKey: number;
  limit?: number;
  /** the session currently open in the Grid, marked rather than duplicated */
  activeSessionId?: string | null;
  className?: string;
}

export function RecentSessionsList({
  unit,
  refreshKey,
  limit = 6,
  activeSessionId = null,
  className,
}: RecentSessionsListProps) {
  const [rows, setRows] = useState<SessionTotalsWire[] | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    void api.sessionHistory(limit).then((result) => {
      if (alive) setRows(result.data);
    });
    return () => {
      alive = false;
    };
  }, [refreshKey, limit]);

  const list = rows ?? [];

  return (
    <section
      data-testid="recent-sessions"
      aria-label="Recent sessions"
      className={className ?? 'flex min-w-0 flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-card'}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline bg-surface-raised px-4 py-3">
        <div className="flex flex-col leading-tight">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">Recent sessions</h2>
          <span className="text-[11px] text-ink-muted">Newest first, straight from your log.</span>
        </div>
        {rows === null ? (
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">Reading…</span>
        ) : (
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            {pluralize(list.length, 'session')}
          </span>
        )}
      </div>

      {rows !== null && list.length === 0 ? (
        <p className="px-4 py-4 text-[12px] text-ink-muted" data-testid="recent-sessions-empty">
          No sessions logged yet. Finish one and it appears here.
        </p>
      ) : null}

      {list.length > 0 ? (
        <ul className="flex flex-col">
          {list.map((session, index) => {
            const meta = STATUS_META[session.status];
            const isCurrent = activeSessionId !== null && session.sessionId === activeSessionId;
            return (
              <li
                key={session.sessionId || `${session.startedAt}-${index}`}
                data-testid={`recent-session-${index}`}
                data-session-id={session.sessionId}
                data-status={session.status}
                className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b border-hairline px-4 py-3 last:border-b-0"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-[14px] font-bold text-ink">
                    {session.label ?? 'Session'}
                    {isCurrent ? <span className="ml-2 text-[10px] uppercase tracking-[0.08em] text-ink-muted">open now</span> : null}
                  </span>
                  <span className="text-[11px] text-ink-muted">{formatDateTime(session.startedAt)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  <span className="l2f-tabular font-mono text-[12px] text-ink-muted">
                    {pluralize(session.setsCompleted, 'set')}
                  </span>
                  <span className="l2f-tabular font-mono text-[12px] font-bold text-ink">
                    {formatVolume(session.volumeKg, unit)}
                  </span>
                  <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
                  {session.sessionId ? (
                    <Link
                      to={`/grid/sessions/${encodeURIComponent(session.sessionId)}`}
                      data-testid={`recent-session-open-${index}`}
                      className="inline-flex h-8 items-center border border-hairline px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                    >
                      Open
                    </Link>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
